/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the patient summary component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';

/**
 * Used to format text.
 */

/**
 * Used to style expanding areas.
 */

/**
 * Used to style expanding areas.
 */

/**
 * Used to style expanding areas.
 */


import { RequestState } from '@ngt/request-utilities';



import CircularProgress from '@material-ui/core/CircularProgress';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { faChevronUp } from '@fortawesome/pro-duotone-svg-icons/faChevronUp';
import Collapse from '@material-ui/core/Collapse';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { IEventType, IFormType, IPatient, useEvents } from '@ngt/online-patient-management-ui';
import PatientSummaryEvent from './PatientSummaryEvent';


/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientSummaryEventsProps {
    patient: IPatient;
    eventType: IEventType;
    formTypes: IFormType[];

}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        paddingLeft: theme.spacing(6)
    },
    item: {
        marginTop: -1,
        border: `1px solid ${theme.palette.grey[300]}`
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


const PatientSummaryEvents: React.FunctionComponent<IPatientSummaryEventsProps> = ({
    patient,
    formTypes,
    eventType
}) => {
    if (!patient) {
        throw new Error('Patient must be defined.');
    }

    if (!eventType) {
        throw new Error('eventType must be defined.');
    }

    if (!formTypes) {
        throw new Error('formTypes must be defined.');
    }

    const classes = useStyles();

    const filteredFormTypes = React.useMemo(() => {
        return formTypes.filter(f => eventType.formTypes.some(ft => ft.formTypeId === f.id && ft?.states.some(fts => fts.patientStatus === patient.status && fts.visible)))
    }, [formTypes, patient, eventType.formTypes]);

    const [events, eventsLoadState, eventsActions] = useEvents(patient.id, patient.institutionId, eventType.id);

    React.useEffect(() => {
        eventsActions.load();

        return () => {
            eventsActions.clear();
        }
    }, [eventsActions]);

    const [expand, setExpand] = React.useState(true);

    const onClick = React.useCallback(() => {
        setExpand(!expand);
    }, [setExpand, expand])


    const loadingEvents = eventsLoadState.state === RequestState.None || eventsLoadState.state === RequestState.Pending;

    const showEventType = React.useMemo(() => {
        return eventType.states.some(ets => ets.visible && ets.patientStatus === patient.status)
    }, [eventType, patient]);

    const components = React.useMemo(() => {
        if (loadingEvents || !showEventType) {
            return null;
        }

        if (events && events.length > 0) {
            if (!eventType.repeating || !eventType.minimumRepeat) {
                return (
                    <>
                        {
                            events.map(event => {
                                return (
                                    <PatientSummaryEvent
                                        key={event.eventRepeat ?? 1}
                                        eventType={eventType}
                                        eventRepeat={event.eventRepeat ?? 1}
                                        event={event}
                                        formTypes={filteredFormTypes}
                                        loadingEvents={loadingEvents}
                                        patient={patient}
                                    />
                                );
                            })
                        }
                    </>
                );
            }

            const minimumArray = Array.from(Array(eventType.minimumRepeat), (e, i) => i + 1);

            return (
                <>
                    {
                        minimumArray.map(i => {
                            const event = events.find(e => e.eventRepeat === i) ?? null;

                            return (
                                <PatientSummaryEvent
                                    key={i}
                                    eventType={eventType}
                                    eventRepeat={i}
                                    event={event}
                                    formTypes={filteredFormTypes}
                                    loadingEvents={loadingEvents}
                                    patient={patient}
                                />
                            );
                        })
                    }
                    {
                        events.filter(event => (event.eventRepeat ?? 1) > (eventType.minimumRepeat ?? 1)).map(event => {
                            return (
                                <PatientSummaryEvent
                                    key={event.eventRepeat ?? 1}
                                    eventType={eventType}
                                    eventRepeat={event.eventRepeat ?? 1}
                                    event={event}
                                    formTypes={filteredFormTypes}
                                    loadingEvents={loadingEvents}
                                    patient={patient}
                                />
                            );
                        })
                    }
                </>
            );
        }

        if (!eventType.repeating) {
            return (
                <PatientSummaryEvent
                    key={1}
                    eventType={eventType}
                    eventRepeat={1}
                    event={null}
                    formTypes={filteredFormTypes}
                    loadingEvents={loadingEvents}
                    patient={patient}
                />
            );
        }

        if (eventType.minimumRepeat) {
            const minimumArray = Array.from(Array(eventType.minimumRepeat), (e, i) => i + 1);

            return (
                <>
                    {
                        minimumArray.map(i => {
                            return (
                                <PatientSummaryEvent
                                    key={i}
                                    eventType={eventType}
                                    eventRepeat={i}
                                    event={null}
                                    formTypes={filteredFormTypes}
                                    loadingEvents={loadingEvents}
                                    patient={patient}
                                />
                            );
                        })
                    }
                </>
            );
        }

        return null;

    }, [events, eventType, filteredFormTypes, patient, loadingEvents, showEventType]);


    if (!showEventType) {
        return null;
    }

    if (loadingEvents) {
        return (
            <ListItem
                button
                className={classes.item}
                onClick={onClick}
            >
                <ListItemIcon>
                    <CircularProgress size={16} />
                </ListItemIcon>
                <ListItemText primary={<strong>{eventType.name}</strong>} />
            </ListItem>
        )
    }

    if (eventType.repeating) {
        return (
            <>
                <ListItem
                    button
                    className={classes.item}
                    onClick={onClick}
                >
                    <ListItemIcon>
                        <FontAwesomeIcon fixedWidth icon={expand ? faChevronUp : faChevronDown} />
                    </ListItemIcon>
                    <ListItemText primary={<strong>{eventType.name}</strong>} />
                </ListItem>
                <Collapse
                    in={expand}
                >
                    <div
                        className={classes.container}
                    >
                        {components}
                    </div>
                </Collapse>
            </>
        );
    }

    return (
        components
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default PatientSummaryEvents;
